/* DirectorShowcase — "Look what AI just made" gallery on the landing page.
 *
 * Shows 8 curated multi-scene director outputs as mini-phone thumbnails
 * rendered from their first scene. Clicking a card pre-fills the AI Reel
 * Director bar with that prompt. Hovering shows a tiny scroller of all N
 * scene thumbnails so the user sees the full story arc.
 *
 * Hypothesis: seeing real outputs before typing raises the click-through on
 * "Generate reel" by 40-60% (social proof).
 */
import { useEffect, useRef, useState } from "react";
import * as fabric from "fabric";
import { Sparkles, Clapperboard, Play } from "lucide-react";
import { adsApi } from "./api";

export default function DirectorShowcase({ onTryPrompt, t = {} }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adsApi.get("/ai/showcase");
        if (!cancelled) setItems(r.data?.showcases || []);
      } catch { /* silent */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;  // Quietly skip during load — don't block landing.
  if (!items.length) return null;

  return (
    <section className="ads-section" style={{ background: "var(--ads-surface)" }}
             data-testid="ads-showcase-section">
      <div className="ads-container">
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <span className="ads-eyebrow" data-testid="ads-showcase-eyebrow">
            <Sparkles size={12} /> {t.studio_showcase_eyebrow || "MADE BY AI IN 10 SECONDS"}
          </span>
          <h2 className="ads-section-title" data-testid="ads-showcase-title">
            {t.studio_showcase_title || "Look what AI just directed."}
          </h2>
          <p className="ads-section-sub" data-testid="ads-showcase-sub">
            {t.studio_showcase_sub || "Real multi-scene reels from a single sentence. Click one to try that prompt — or write your own."}
          </p>
        </div>

        <div className="ads-showcase-grid" data-testid="ads-showcase-grid">
          {items.map((s) => (
            <ShowcaseCard key={s.id} showcase={s} onTryPrompt={onTryPrompt} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowcaseCard({ showcase, onTryPrompt, t }) {
  // Auto-play scene preview on hover: cycles through scenes as a cheap
  // "MP4 preview" (no real video encoding — just CSS crossfade between
  // pre-rendered scene canvases). Turns a static card into a living trailer.
  const [hovered, setHovered] = useState(false);
  const [frame, setFrame] = useState(0);
  const total = showcase.scenes?.length || 1;

  useEffect(() => {
    if (!hovered || total < 2) return;
    const id = setInterval(() => setFrame((f) => (f + 1) % total), 850);
    return () => clearInterval(id);
  }, [hovered, total]);

  // Reset to scene 0 when hover ends so the next hover starts at the hook.
  useEffect(() => { if (!hovered) setFrame(0); }, [hovered]);

  return (
    <div
      className={`ads-showcase-card ${hovered ? "is-playing" : ""}`}
      data-testid={`ads-showcase-card-${showcase.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="ads-showcase-phone">
        <div className="ads-showcase-notch" />

        {/* Stacked scene canvases — only the active frame is visible via
            opacity. Fabric rendering cost is paid once on mount; cycling is
            pure CSS. This gives users a believable MP4-like preview without
            the MediaRecorder round-trip. */}
        <div className="ads-showcase-stack">
          {showcase.scenes.map((sc, i) => (
            <div
              key={sc.scene_id}
              className="ads-showcase-frame"
              style={{ opacity: i === frame ? 1 : 0 }}
              data-testid={`ads-showcase-frame-${showcase.id}-${i}`}
            >
              <SceneCanvas
                canvasJson={sc.canvas_json}
                width={showcase.width}
                height={showcase.height}
              />
            </div>
          ))}
        </div>

        {/* Per-frame progress bar — tiny timeline that fills across frames */}
        <div className="ads-showcase-progress" aria-hidden="true">
          {showcase.scenes.map((_, i) => (
            <div
              key={i}
              className={`ads-showcase-progress-seg ${hovered && i <= frame ? "is-active" : ""}`}
            />
          ))}
        </div>

        <div className="ads-showcase-duration" data-testid={`ads-showcase-duration-${showcase.id}`}>
          <Clapperboard size={10} /> {showcase.scene_count} · {(showcase.duration_ms / 1000).toFixed(0)}s
        </div>
      </div>

      <div className="ads-showcase-meta">
        <div className="ads-showcase-industry" data-testid={`ads-showcase-industry-${showcase.id}`}>
          {showcase.industry}
        </div>
        <div className="ads-showcase-title" data-testid={`ads-showcase-title-${showcase.id}`}>
          {showcase.title}
        </div>
        <div className="ads-showcase-prompt" title={showcase.prompt}>
          "{showcase.prompt}"
        </div>
      </div>

      <button
        type="button"
        onClick={() => onTryPrompt?.(showcase)}
        className="ads-showcase-cta"
        data-testid={`ads-showcase-try-${showcase.id}`}
      >
        <Play size={13} /> {t.studio_showcase_try || "Try this prompt"}
      </button>
    </div>
  );
}

/** Paint a small Fabric preview of a scene's canvas_json. */
function SceneCanvas({ canvasJson, width, height, size = 200, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !canvasJson) return;
    const aspect = (canvasJson.height || height || 1920) / (canvasJson.width || width || 1080);
    const w = size;
    const h = Math.round(w * aspect);
    el.width = w; el.height = h;
    const c = new fabric.StaticCanvas(el, {
      width: w, height: h,
      backgroundColor: canvasJson.background || "#0b0b12",
    });
    const scale = w / (canvasJson.width || 1080);
    (canvasJson.objects || []).slice(0, 6).forEach((o) => {
      let fo = null;
      // Source canvas_json was designed for full 1080×1920 phone reels
      // where some objects sit slightly off-canvas as a design choice
      // (negative left, etc.). In a 200-px thumbnail those offsets read
      // as broken text bleeding outside the phone frame. Clamp each
      // object's left/top to the visible rect and shrink its width to
      // whatever room remains. This is the only way to make the small
      // preview look intentional — Fabric's clipPath alone won't crop
      // text that wraps off-canvas, it just hides pixels.
      const rawLeft = (o.left || 0) * scale;
      const rawTop  = (o.top  || 0) * scale;
      const left = Math.max(0, rawLeft);
      const top  = Math.max(0, rawTop);
      const leftPadded = Math.min(left, Math.max(0, w - 10));
      const topPadded  = Math.min(top,  Math.max(0, h - 6));
      const common = {
        left: leftPadded,
        top: topPadded,
        opacity: o.opacity ?? 1,
        selectable: false, evented: false,
        // The source canvas_json was authored with originX/originY varying
        // per object (some centred, some left-anchored). For a tiny 200-px
        // preview we want predictable left-edge positioning so the clamp
        // above means what it says — force every object to anchor from
        // its top-left, regardless of how the master file was built.
        originX: "left",
        originY: "top",
      };
      if (o.type === "textbox" || o.type === "text" || o.type === "i-text") {
        const availableW = Math.max(40, w - leftPadded - 4);
        // Use Textbox so the title wraps onto a second line inside the
        // phone frame rather than bleeding off the left edge.
        fo = new fabric.Textbox((o.text || "").slice(0, 80), {
          ...common,
          fontSize: Math.max(4, (o.fontSize || 24) * scale),
          fill: o.fill || "#fff",
          fontWeight: o.fontWeight || "normal",
          fontFamily: o.fontFamily || "Inter",
          width: Math.min(availableW, Math.max(40, (o.width || 200) * scale)),
          textAlign: o.textAlign || "left",
          splitByGrapheme: false,
        });
      } else if (o.type === "rect") {
        fo = new fabric.Rect({
          ...common,
          width: (o.width || 10) * scale,
          height: (o.height || 10) * scale,
          fill: o.fill || "#333",
          rx: (o.rx || 0) * scale,
          ry: (o.ry || 0) * scale,
        });
      } else if (o.type === "circle") {
        fo = new fabric.Circle({
          ...common,
          radius: (o.radius || 10) * scale,
          fill: o.fill || "#333",
        });
      }
      if (fo) c.add(fo);
    });
    c.renderAll();
    return () => { try { c.dispose(); } catch {} };
  }, [canvasJson, width, height, size]);

  return <canvas ref={ref} data-testid={testid} />;
}
